import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";

const LS_KEY = "glp1_intake_step7";

const LS_KEY_RECOMMENDATION = "Rrecommendation"; // affordability | potency
const LS_KEY_RECOMMENDED_TYPE = "recommendedType"; // injection | tablet

// ✅ API-ready question keys
const Q7_KEY = "Q7_Which_of_these_is_most_important_to_you";
const Q7A_KEY =
  "Q7a_GLP1_is_available_as_an_injection_or_a_dissolvable_tablet_which_sounds_best";

// ✅ FULL answers (store these exact strings)
const Q7_ANSWERS = {
  AFFORDABILITY: "Affordability (Lowest price)",
  POTENCY: "Potency (Stronger dose)",
} as const;

const Q7A_ANSWERS = {
  INJECT: "I prefer to inject (One injection per week)",
  TABLET: "I prefer a tablet (One tablet per day)",
} as const;

type Q7Answer = (typeof Q7_ANSWERS)[keyof typeof Q7_ANSWERS] | "";
type Q7AAnswer = (typeof Q7A_ANSWERS)[keyof typeof Q7A_ANSWERS] | "";

type StoredPayload = {
  [Q7_KEY]?: Q7Answer;
  [Q7A_KEY]?: Q7AAnswer;
};

// UI-only state (minimal)
type FormState = {
  q7: Q7Answer;
  q7a: Q7AAnswer;
};

const DEFAULT: FormState = { q7: "", q7a: "" };

type OptionCardProps = {
  name: string;
  label: string;
  subLabel?: string;
  checked: boolean;
  icon: React.ReactNode;
  onChange: () => void;
};

const OptionCard: React.FC<OptionCardProps> = ({
  name,
  label,
  subLabel,
  checked,
  icon,
  onChange,
}) => {
  return (
    <button
      type="button"
      className={`funnel-option funnel-option-btn funnel-checkbox funnel-tile ${
        checked ? "is-selected" : ""
      }`}
      onClick={onChange}
    >
      <div className="funnel-tile__icon" aria-hidden="true">
        {icon}
      </div>

      <div className="funnel-tile__bottom">
        <span
          className={`funnel-control radio ${checked ? "is-selected" : ""}`}
        >
          <span className="dot" />
        </span>

        <div className="funnel-tile__text">
          <div className="funnel-tile__label">{label}</div>
        </div>

        <input
          className="sr-only"
          type="radio"
          name={name}
          checked={checked}
          onChange={onChange}
        />
      </div>

      {subLabel ? (
        <div className="funnel-tile__sublabel">{subLabel}</div>
      ) : null}
    </button>
  );
};

const ReceiptIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="funnel-svg-icon"
    viewBox="0 0 24 24"
    stroke="currentColor"
    fill="none"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
    <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2"></path>
    <path d="M14 8h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5m2 0v1.5m0 -9v1.5"></path>
  </svg>
);

const TrendingUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="funnel-svg-icon"
    viewBox="0 0 24 24"
    stroke="currentColor"
    fill="none"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
    <polyline points="3 17 9 11 13 15 21 7"></polyline>
    <polyline points="14 7 21 7 21 14"></polyline>
  </svg>
);

const VaccineIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="funnel-svg-icon"
    viewBox="0 0 24 24"
    stroke="currentColor"
    fill="none"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
    <path d="M17 3l4 4"></path>
    <path d="M19 5l-4.5 4.5"></path>
    <path d="M11.5 6.5l6 6"></path>
    <path d="M16.5 11.5l-6.5 6.5h-4v-4l6.5 -6.5"></path>
    <path d="M7.5 12.5l1.5 1.5"></path>
    <path d="M10.5 9.5l1.5 1.5"></path>
    <path d="M3 21l3 -3"></path>
  </svg>
);

const MilkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="funnel-svg-icon"
    viewBox="0 0 24 24"
    stroke="currentColor"
    fill="none"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
    <path d="M8 6h8v-2a1 1 0 0 0 -1 -1h-6a1 1 0 0 0 -1 1v2z"></path>
    <path d="M16 6l1.094 1.759a6 6 0 0 1 .906 3.17v8.071a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2v-8.071a6 6 0 0 1 .906 -3.17l1.094 -1.759"></path>
    <circle cx="12" cy="16" r="2"></circle>
    <path d="M10 10h4"></path>
  </svg>
);

// ✅ safe read
const readFromLS = (): FormState => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT;

    const parsed = JSON.parse(raw) as StoredPayload;

    const q7 = parsed?.[Q7_KEY];
    const q7a = parsed?.[Q7A_KEY];

    const safeQ7: Q7Answer =
      q7 === Q7_ANSWERS.AFFORDABILITY || q7 === Q7_ANSWERS.POTENCY ? q7 : "";

    const safeQ7A: Q7AAnswer =
      q7a === Q7A_ANSWERS.INJECT || q7a === Q7A_ANSWERS.TABLET ? q7a : "";

    // if q7 missing, q7a must be empty
    if (!safeQ7) return DEFAULT;

    return { q7: safeQ7, q7a: safeQ7A };
  } catch {
    return DEFAULT;
  }
};

// ✅ payload builder
const buildPayload = (form: FormState): StoredPayload => {
  const payload: StoredPayload = {};

  if (form.q7) payload[Q7_KEY] = form.q7;
  if (form.q7 && form.q7a) payload[Q7A_KEY] = form.q7a;

  return payload;
};

const NewFunnelStep7: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(DEFAULT);
  const [hydrated, setHydrated] = useState(false);

  const recommendationValue = (q7: Q7Answer) =>
    q7 === Q7_ANSWERS.AFFORDABILITY
      ? "affordability"
      : q7 === Q7_ANSWERS.POTENCY
      ? "potency"
      : "";

  const recommendedTypeValue = (q7a: Q7AAnswer) =>
    q7a === Q7A_ANSWERS.INJECT
      ? "injection"
      : q7a === Q7A_ANSWERS.TABLET
      ? "tablet"
      : "";

  // ✅ show Q7a only after click in this session
  const [revealedFormat, setRevealedFormat] = useState(false);

  // ✅ hydrate once
  useEffect(() => {
    const fromLS = readFromLS();
    setForm(fromLS);

    // ✅ IMPORTANT FIX
    if (fromLS.q7) {
      setRevealedFormat(true);
    }

    setHydrated(true);
  }, []);
  // ✅ save only after hydration + after user has selected something
  useEffect(() => {
    if (!hydrated) return;
    if (!form.q7) return;

    localStorage.setItem(LS_KEY, JSON.stringify(buildPayload(form)));
  }, [form, hydrated]);

  // ✅ if q7 cleared/changed, clear q7a
  const setQ7 = (val: Q7Answer) => {
    setRevealedFormat(true);
    setForm((p) => ({
      q7: val,
      q7a: p.q7 === val ? p.q7a : "", // reset q7a when switching q7
    }));
  };

  const showFormat = revealedFormat && Boolean(form.q7);
  const canGoNext = Boolean(form.q7) && Boolean(form.q7a);

  const next = () => {
    if (!canGoNext) return;

    // ✅ force save on navigation (extra-safe)
    localStorage.setItem(LS_KEY, JSON.stringify(buildPayload(form)));

    // ✅ separate keys (new)
    localStorage.setItem(LS_KEY_RECOMMENDATION, recommendationValue(form.q7));
    localStorage.setItem(
      LS_KEY_RECOMMENDED_TYPE,
      recommendedTypeValue(form.q7a)
    );

    navigate("/glp1/intake-form/step-7");
  };

  return (
    <FunnelLayout>
      <h1
        className="title-step-1 funnel-q-title-heading"
        style={{ color: "#000" }}
      >
        <span>Looking good!</span> Let&apos;s match you with the best
        medication.
      </h1>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title" style={{ marginBottom: "16px" }}>
          Which of these is most important to you?
        </div>

        <div className="funnel-grid-2">
          <OptionCard
            name="q7"
            label="Affordability"
            subLabel="Lowest price"
            icon={<ReceiptIcon />}
            checked={form.q7 === Q7_ANSWERS.AFFORDABILITY}
            onChange={() => setQ7(Q7_ANSWERS.AFFORDABILITY)}
          />

          <OptionCard
            name="q7"
            label="Potency"
            subLabel="Stronger dose"
            icon={<TrendingUpIcon />}
            checked={form.q7 === Q7_ANSWERS.POTENCY}
            onChange={() => setQ7(Q7_ANSWERS.POTENCY)}
          />
        </div>
      </div>

      {showFormat && (
        <>
          <div className="funnel-divider" />

          <div className="funnel-section">
            <div className="funnel-q-title">
              GLP-1 is available as an injection or a dissolvable tablet. Which
              sounds best?
            </div>

            <div className="funnel-grid-2">
              <OptionCard
                name="q7a"
                label="I prefer to inject"
                subLabel="One injection per week"
                icon={<VaccineIcon />}
                checked={form.q7a === Q7A_ANSWERS.INJECT}
                onChange={() =>
                  setForm((p) => ({ ...p, q7a: Q7A_ANSWERS.INJECT }))
                }
              />

              <OptionCard
                name="q7a"
                label="I prefer a tablet"
                subLabel="One tablet per day"
                icon={<MilkIcon />}
                checked={form.q7a === Q7A_ANSWERS.TABLET}
                onChange={() =>
                  setForm((p) => ({ ...p, q7a: Q7A_ANSWERS.TABLET }))
                }
              />
            </div>
          </div>
        </>
      )}

      <button className="next-btn" onClick={next} disabled={!canGoNext}>
        Next →
      </button>
    </FunnelLayout>
  );
};

export default NewFunnelStep7;
